import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VehicleInsuranceService from "../services/vehicleInsuranceService";
import StorageService from "../services/StorageService";
import Breadcrumb from "../components/Breadcrumb";
import Loading from "../components/loading";
import ReactPaginate from "react-paginate";
import XLSX from "xlsx-js-style";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import utility from "../utils/utility";
import MessageDialog from "../components/MessageDialog";

export default function ViewVehicleInsurance() {
  const [insurances, setInsurances] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  // const messageTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Format date to DD/MM/YYYY
  // const formatDate = (dateStr) => {
  //   if (!dateStr) return "-";
  //   const date = new Date(dateStr);
  //   return date.toLocaleDateString("en-GB");
  // };

  // const fetchData = async () => {
  //   setLoading(true);
  //   try {
  //     const companyId = StorageService.activeCompany().companyId;
  //     const res = await VehicleInsuranceService.getByCompanyId(companyId);
  //     const data = res.data || res || [];
  //     setInsurances(data.sort((a, b) => b.id - a.id));
  //   } catch (err) {
  //     console.error("Error fetching vehicle insurance:", err);
  //     showMessage("Failed to load insurance data", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const fetchData = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const search = searchText.trim().toLowerCase();

      const res = search
        ? await VehicleInsuranceService.exportVehicleInsurance(companyId)
        : await VehicleInsuranceService.getByCompanyId(
          companyId,
          currentPage,
          itemsPerPage,
          searchText
        );

      let data = res.data || [];

      if (search) {
        data = data.filter(
          (i) =>
            i.vehicleName?.toLowerCase().includes(search) ||
            i.registrationNo?.toLowerCase().includes(search)
        );
      }

      setInsurances(search ? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : data);
      setPageCount(search ? Math.ceil(data.length / itemsPerPage) : res.totalPages || 0);

    } catch (err) {
      console.error("Error fetching vehicle insurance:", err);
      showMessage("Failed to load insurance data", "error");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchData();
  // }, [currentPage]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  // Refresh after add / edit
  useEffect(() => {
    if (location.state?.refresh) {
      fetchData();
      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  const exportExcel = async () => {
    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    try {
      const company = StorageService.activeCompany();

      const res = await VehicleInsuranceService.exportVehicleInsurance(
        company.companyId
      );

      if (!res?.status || !res.data.length) {
        showMessage("No data to export", "error");
        return;
      }

      const insurances = res.data;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;

      // ✅ STYLES
      const thin = { style: "thin" };
      const thick = { style: "medium" };

      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      };

      // =========================
      // TITLE
      // =========================
      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Insurance Report"]], {
        origin: { r: row, c: 1 },
      });

      ws["!merges"].push({
        s: { r: row, c: 1 },
        e: { r: row, c: 7 },
      });

      const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: center,
      };

      row += 2;

      // =========================
      // HEADER
      // =========================
      const header = [
        "Company",
        "Vehicle",
        "Registration No",
        "Insurance Date",
        "Next Insurance Date",
        "Status",
      ];

      const startRow = row; // ✅ START TRACK

      XLSX.utils.sheet_add_aoa(ws, [header], {
        origin: { r: row, c: 1 },
      });

      header.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: i + 1 });

        ws[cell].s = {
          font: { bold: true },
          alignment: center,
        };
      });

      row++;

      // =========================
      // DATA
      // =========================
      insurances.forEach((v) => {
        const rowData = [
          v.companyName,
          v.vehicleName,
          v.registrationNo,
          v.insuranceDate
            ? utility.formatDateDDMMMYYYY(v.insuranceDate)
            : "-",

          v.nextInsuranceDate
            ? utility.formatDateDDMMMYYYY(v.nextInsuranceDate)
            : "-",
          v.status,
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], {
          origin: { r: row, c: 1 },
        });

        row++;
      });

      const endRow = row - 1; // ✅ END TRACK

      // =========================
      // APPLY BORDERS 🔥
      // =========================
      const colCount = header.length;

      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });

          if (!ws[cell]) {
            ws[cell] = { t: "s", v: "" };
          }

          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = center;

          ws[cell].s.border = {
            top: r === startRow ? thick : thin,
            bottom: r === endRow ? thick : thin,
            left: c === 1 ? thick : thin,
            right: c === colCount ? thick : thin,
          };
        }
      }

      // =========================
      // COLUMN WIDTH
      // =========================
      ws["!cols"] = [
        { wch: 20 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 12 },
      ];

      // =========================
      // EXPORT
      // =========================
      XLSX.utils.book_append_sheet(wb, ws, "VehicleInsurance");

      XLSX.writeFile(wb, "VehicleInsurance.xlsx");
    } catch (error) {
      console.error(error);
      showMessage("Error exporting data", "error");
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const { permission, checkView, can } = usePermission(
    ScreenData.Vehicle_Insurance,
    showMessage
  );

  useEffect(() => {
    if (permission) {
      if (checkView()) {
        fetchData();
      }
    }
  }, [currentPage, permission, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);
  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("No permission", "error");
      return;
    }
    navigate("/cpnl/vehicleinsurance/add");
  };

  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }

    if (!selected) {
      showMessage("Please select one vehicle insurance to modify.");
      return;
    }
    navigate(`/cpnl/vehicleinsurance/add/${selected}`);
  };

  const handleToggleStatus = async (insurance) => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    const newStatus = insurance.status === "Active" ? "Inactive" : "Active";

    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`))
      return;

    setLoading(true);
    try {
      const res = await VehicleInsuranceService.updateStatus(insurance.id);
      showMessage(res?.message || "Status updated successfully", "success");
      fetchData();
    } catch (err) {
      console.error("Error updating status:", err);
      showMessage("Failed to update status", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 RADIO SELECT HANDLER
  const handleSelect = (id) => {
    setSelected(id);
  };

  // 🔹 FRONTEND SEARCH FILTER
  const filteredInsurances = insurances;

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Insurance" secondTitle="Vehicle Management" />

      {/* {message && (
        <div
          className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
            } d-flex justify-content-between align-items-center mb-2 p-2`}
        >
          <span>{message}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
          ></button>
        </div>
      )} */}

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />


      <div className="row">
        <div className="card p-3 bg-white shadow-sm">
          {/* HEADER BUTTONS */}
          {/* <div className="mb-3 pb-2 border-bottom">
            <button
              className="d-inline-flex btn btn-success me-2"
              onClick={handleAdd}
              disabled={loading}
            >
              <i className="ti ti-plus me-1"></i> Add
            </button>

            <button
              className="d-inline-flex btn btn-warning me-2"
              onClick={handleModify}
              disabled={loading}
            >
              <i className="ti ti-pencil me-1"></i> Modify
            </button>
          </div> */}

          {/* 🔹 SEARCH + BUTTONS */}
          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">

            {/* SEARCH FIELD */}
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Vehicle"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* BUTTONS */}

            <div className="ms-auto d-flex gap-2">

              {can("canAdd") && (
                <button
                  className="d-inline-flex btn btn-success"
                  onClick={handleAdd}
                  disabled={loading}
                >
                  <i className="ti ti-plus me-1"></i> Add
                </button>
              )}

              {can("canModify") && (
                <button
                  className="d-inline-flex btn btn-warning"
                  onClick={handleModify}
                  disabled={loading}
                >
                  <i className="ti ti-pencil me-1"></i> Modify
                </button>
              )}

              {can("canView") && (
                <button className="btn btn-success" onClick={exportExcel}>
                  <i className="ti ti-download me-1"></i>
                  Export Excel
                </button>
              )}

            </div>


          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Vehicle</th>
                  <th>Insurance Date</th>
                  <th>Next Insurance Date</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                )
                  : filteredInsurances.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No vehicle insurance records found
                      </td>
                    </tr>
                  ) : (
                    filteredInsurances.map((insurance) => {
                      const isActive = insurance.status === "Active";
                      return (
                        <tr key={insurance.id}>
                          <td>
                            <input
                              type="radio"
                              name="insuranceSelect"
                              disabled={!isActive}
                              checked={selected === insurance.id}
                              onChange={() => handleSelect(insurance.id)}
                            />
                          </td>
                          <td>{insurance.vehicleName || "-"}-{insurance.registrationNo || "-"}</td>
                          <td>{utility.formatDateDDMMMYYYY(insurance.insuranceDate)}</td>
                          <td>{utility.formatDateDDMMMYYYY(insurance.nextInsuranceDate)}</td>
                          <td>
                            <span
                              className={`badge ${isActive ? "bg-success" : "bg-secondary"
                                }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleToggleStatus(insurance)}
                            >
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
              </tbody>
            </table>
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />
          </div>
        </div>
      </div>
    </>
  );
}
